import { TICKS_PER_SECOND, type Layout } from "@empire/core-sim";
import { create } from "zustand";
import type { DryRun, Frame } from "../sim/dryRun.js";
import { testAgainstPresets } from "../sim/runner.js";

/**
 * The editor's Test panel and its mini replay (S2-5).
 *
 * Testing is free and unlimited by design: the whole point of the defence
 * editor is that a defender can find out their maze is bad BEFORE a real
 * attacker does, and anything that rations that would push people back to
 * guessing.
 */

export type TestStatus = "idle" | "running" | "done" | "error";

/** Playback speeds offered, per S18. */
export const SPEEDS = [1, 2, 4] as const;
export type Speed = (typeof SPEEDS)[number];

const MS_PER_TICK = 1000 / TICKS_PER_SECOND;

interface TestState {
  status: TestStatus;
  error: string | null;
  runs: DryRun[];
  /** Which preset's replay is on the board, if any. */
  playing: string | null;
  paused: boolean;
  speed: Speed;
  /** Fractional frame index, so playback is smooth at any speed. */
  position: number;

  test: (layout: Layout) => Promise<void>;
  play: (presetId: string) => void;
  togglePause: () => void;
  setSpeed: (speed: Speed) => void;
  stop: () => void;
  /** Called from the render loop. Returns the frame to draw, or null. */
  advance: (deltaMs: number) => Frame | null;
  /** The layout changed under us; results no longer describe what is on screen. */
  invalidate: () => void;
}

export const useTestStore = create<TestState>((set, get) => ({
  status: "idle",
  error: null,
  runs: [],
  playing: null,
  paused: false,
  speed: 1,
  position: 0,

  test: async (layout) => {
    set({ status: "running", error: null, playing: null });
    try {
      const runs = await testAgainstPresets(layout);
      set({ status: "done", runs });
    } catch (err) {
      set({
        status: "error",
        error: err instanceof Error ? err.message : String(err),
        runs: [],
      });
    }
  },

  play: (presetId) => set({ playing: presetId, position: 0, paused: false }),

  togglePause: () => set({ paused: !get().paused }),

  setSpeed: (speed) => set({ speed }),

  stop: () => set({ playing: null, position: 0, paused: false }),

  advance: (deltaMs) => {
    const { playing, runs, paused, speed, position } = get();
    if (!playing) return null;
    const run = runs.find((r) => r.presetId === playing);
    if (!run || run.frames.length === 0) return null;

    const next = paused ? position : position + (deltaMs * speed) / MS_PER_TICK;
    if (next >= run.frames.length) {
      // Hold on the last frame rather than snapping to an empty board: the
      // final moment is the one worth looking at.
      set({ position: run.frames.length - 1, paused: true });
      return run.frames[run.frames.length - 1] ?? null;
    }
    if (next !== position) set({ position: next });
    return run.frames[Math.floor(next)] ?? null;
  },

  invalidate: () =>
    set({ status: "idle", runs: [], playing: null, position: 0, error: null }),
}));
